import { i18n, Resource } from 'i18next';
import { useLocale } from '@tanium/react-locale-context';
import { generateTrans, TransWrapperType } from './generateTrans';
import {
  generateUseTranslation,
  StrictUseTranslation,
  UseTranslation,
} from './generateUseTranslation';
import { getCalculatedLocale } from './getCalculatedLocale';
import { i18nFactory } from './i18nFactory';

interface StrictTranslationTools<TPackageName extends string> {
  Trans: TransWrapperType;
  useTranslation: StrictUseTranslation<TPackageName>;
  useCalculatedWorkbenchLocale: () => string;
}
interface LooseTranslationTools {
  Trans: TransWrapperType;
  useTranslation: UseTranslation;
  useCalculatedWorkbenchLocale: () => string;
}

export function generateTranslationTools(
  packageName: string,
  resources: Resource,
): LooseTranslationTools;
export function generateTranslationTools<TPackageName extends string>(
  packageName: TPackageName,
  resources: Resource,
  options: { strictTypes: true },
): StrictTranslationTools<TPackageName>;
export function generateTranslationTools(packageName: string, resources: Resource) {
  // This state is stored here, outside a react component, the first time an @tanium package is used.
  // That way, we have 'global space per package', so i18n can create/init only once per package.
  const i18nInstance: i18n = i18nFactory(packageName, resources);

  const useTranslation = generateUseTranslation(i18nInstance, packageName, resources);
  const Trans = generateTrans(i18nInstance, useTranslation);
  const useCalculatedWorkbenchLocale = () => getCalculatedLocale(resources, useLocale());

  return {
    Trans,
    useTranslation,
    useCalculatedWorkbenchLocale,
  };
}
